import { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Header from './components/Header';
import SubHeader from './components/SubHeader';
import TokenInput from './components/TokenInput';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import LoadingSpinner from './components/LoadingSpinner';
import ResultDisplay from './components/ResultDisplay';
import OpenGraphMeta from './components/OpenGraphMeta';
import BackgroundVideo from './components/BackgroundVideo';
import { fetchTokenAnalysis, type TokenAnalysisResult } from './api/bipApi';
import { fetchTokenStatesWithNotes } from './api/bipApi';
import { useBgm } from './hooks/useBgm';
import { useSearchHistory } from './hooks/useSearchHistory';
import { useAppStore } from './store/useAppStore';
import type { HeadlineToken } from './components/HeadlineItem';
import { getOgImageUrlForToken } from './config/opengraph';
import { useTokenImageGradient } from './hooks/useTokenImageGradient';

const SEARCH_PATH = '/search';
const KEYWORD_PARAM = 'keyword';

function getKeywordFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  if (pathname !== SEARCH_PATH) return null;
  const params = new URLSearchParams(search);
  const kw = params.get(KEYWORD_PARAM);
  return kw && kw.trim() ? kw.trim() : null;
}

/** URL에서 검색 키워드/섹션을 읽어 OG용으로 사용 (첫 렌더부터 동적 og:image 적용) */
function getOgConfigFromLocation():
  | { image: string; url: string }
  | undefined {
  if (typeof window === 'undefined') return undefined;
  const { pathname, search } = window.location;
  if (pathname !== SEARCH_PATH) return undefined;
  const params = new URLSearchParams(search);
  const keyword = params.get(KEYWORD_PARAM)?.trim();
  if (!keyword) return undefined;
  const section = params.get('section');
  return {
    image: getOgImageUrlForToken(keyword, section),
    url: window.location.href,
  };
}

function App() {
  const { isSearchMode, setSelectedAddress, setSearchMode } = useAppStore();
  const { saveToHistory } = useSearchHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TokenAnalysisResult | null>(null);
  const [resetting, setResetting] = useState(false);
  const [headlineTokens, setHeadlineTokens] = useState<HeadlineToken[]>([]);
  const { startBgm, isMuted, toggleMuted } = useBgm();
  const hasStartedBgm = useRef(false);
  const hasSyncedFromUrl = useRef(false);
  const searchAbortRef = useRef<AbortController | null>(null);

  const tokenImageUrl = result?.token?.imageUrl ?? null;
  const gradientColors = useTokenImageGradient(tokenImageUrl);

  // 헤드라인 티커용 토큰 목록 (token-states/with-notes API)
  useEffect(() => {
    let cancelled = false;
    fetchTokenStatesWithNotes(12)
      .then((res) => {
        if (cancelled) return;
        const tokens: HeadlineToken[] = res.data.map((item) => ({
          symbol: item.tokenSymbol || '—',
          address: item.tokenAddress,
          imageUrl: item.imageUrl ?? undefined,
        }));
        setHeadlineTokens(tokens);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to fetch headline tokens:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // URL → state: 초기 로드 및 브라우저 뒤로가기/앞으로가기. 검색 중 메인으로 돌아가면 요청 즉시 중단.
  const loadTokenAnalysis = useCallback(
    async (tokenAddress: string) => {
      searchAbortRef.current?.abort();
      searchAbortRef.current = new AbortController();
      const signal = searchAbortRef.current.signal;

      setIsLoading(true);
      setResult(null);
      try {
        const data = await fetchTokenAnalysis(tokenAddress, signal);
        if (signal.aborted) return;
        setResult(data);
        saveToHistory(tokenAddress, {
          symbol: data.symbol,
          imageUrl: data.imageUrl ?? undefined,
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Failed to fetch token analysis:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [saveToHistory],
  );

  useEffect(() => {
    if (hasSyncedFromUrl.current) return;
    hasSyncedFromUrl.current = true;
    const keyword = getKeywordFromLocation();
    if (keyword) {
      setSelectedAddress(keyword);
      setSearchMode(true);
      loadTokenAnalysis(keyword);
    }
  }, [loadTokenAnalysis, setSelectedAddress, setSearchMode]);

  useEffect(() => {
    const handlePopState = () => {
      const keyword = getKeywordFromLocation();
      if (keyword) {
        setSelectedAddress(keyword);
        setSearchMode(true);
        loadTokenAnalysis(keyword);
      } else {
        searchAbortRef.current?.abort();
        setSearchMode(false);
        setSelectedAddress('');
        setResult(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [loadTokenAnalysis, setSelectedAddress, setSearchMode]);

  // 로고/브랜드 클릭 시 결과 페이드아웃 후 완전 초기화
  useEffect(() => {
    if (!resetting) return;
    const t = setTimeout(() => {
      setSearchMode(false);
      setSelectedAddress('');
      setResult(null);
      setResetting(false);
      window.history.pushState({}, '', '/');
    }, 320);
    return () => clearTimeout(t);
  }, [resetting, setSearchMode, setSelectedAddress]);

  // First click anywhere starts BGM (capture so we run before other handlers)
  useEffect(() => {
    const handleFirstClick = () => {
      if (hasStartedBgm.current) return;
      hasStartedBgm.current = true;
      startBgm();
      document.removeEventListener('click', handleFirstClick, true);
    };
    document.addEventListener('click', handleFirstClick, true);
    return () => document.removeEventListener('click', handleFirstClick, true);
  }, [startBgm]);

  // Theme state with localStorage persistence
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme === 'light' ? 'light' : 'dark') as 'light' | 'dark';
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // 검색 전: html/body 스크롤 비활성화 (검색 모드일 때만 스크롤 가능)
  useEffect(() => {
    if (!isSearchMode) {
      document.documentElement.classList.add('no-scroll');
    } else {
      document.documentElement.classList.remove('no-scroll');
    }
    return () => document.documentElement.classList.remove('no-scroll');
  }, [isSearchMode]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const handleReset = () => {
    searchAbortRef.current?.abort();
    setResetting(true);
  };

  // Single entry for all search triggers (TokenInput submit, Header headline click). Save history only on API success.
  const handleTokenSubmit = useCallback(
    (tokenAddress: string) => {
      setSelectedAddress(tokenAddress);
      setSearchMode(true);
      const url = `${SEARCH_PATH}?${KEYWORD_PARAM}=${encodeURIComponent(tokenAddress)}`;
      window.history.pushState({}, '', url);
      loadTokenAnalysis(tokenAddress);
    },
    [loadTokenAnalysis, setSelectedAddress, setSearchMode],
  );

  // URL 기준으로 OG 설정 (첫 렌더부터 적용되도록 state가 아닌 location 사용)
  const ogConfig = getOgConfigFromLocation();

  return (
    <>
      <OpenGraphMeta config={ogConfig} />
      <BackgroundVideo visible={!isSearchMode} />
      <div className={`container ${!isSearchMode ? 'container--bg-video' : ''}`}>
        <Header
          isSearchMode={isSearchMode && !resetting}
          onReset={handleReset}
          onSearchToken={handleTokenSubmit}
          headlineTokens={headlineTokens.length > 0 ? headlineTokens : undefined}
        />
        {result && !resetting ? (
          <SubHeader result={result} />
        ) : null}
        <div className="content-and-results-wrap">
          {tokenImageUrl && (
            <div className="search-result-pixel-bg" aria-hidden>
              <div
                className="search-result-pixel-bg-inner"
                style={{ backgroundImage: `url(${tokenImageUrl})` }}
              />
            </div>
          )}
          {(gradientColors || theme === 'light') && (
            <div
              className="search-result-gradient"
              style={{
                background:
                  theme === 'light'
                    ? 'linear-gradient(to bottom, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 40%, transparent 100%)'
                    : `linear-gradient(to bottom, ${gradientColors!.color1} 0%, ${gradientColors!.color2} 40%, transparent 100%)`,
              }}
              aria-hidden
            />
          )}
          <div className={`content ${isSearchMode && !resetting ? 'moved-up' : ''}`}>
            <div className={`intro-logo-block ${isSearchMode && !resetting ? 'intro-logo-block--faded' : ''}`} aria-hidden="true">
              <img src="/static/logo_elon_circle.png" alt="" className="intro-logo-block-logo" />
              <h1 className="intro-logo-block-brand">MONEYBALL</h1>
            </div>
            <p className={`subtitle ${isSearchMode && !resetting ? 'subtitle-faded' : ''}`}>
              Find value in memes<br />that nobody else can see.
            </p>
            <TokenInput onSubmit={handleTokenSubmit} />
          </div>

          {(isSearchMode || resetting) && (
            <div className={`results-area ${resetting ? 'results-area--fade-out' : ''}`}>
              {isLoading && !resetting ? (
                <LoadingSpinner />
              ) : result ? (
                <ResultDisplay result={result} />
              ) : null}
            </div>
          )}
        </div>

        {isSearchMode && result && !resetting && <BackToTop />}
        <Footer
          theme={theme}
          onThemeToggle={toggleTheme}
          bgmMuted={isMuted}
          onBgmToggle={toggleMuted}
          enableScrollFade={!!(isSearchMode && result && !resetting)}
        />
      </div>
    </>
  );
}

export default App;
